import React from 'react';
import { X, Calendar, PhoneCall, MapPin, AlertTriangle, UserX, Users, TrendingUp, Target, FileText, Clock, AlertOctagon, FileQuestion } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { formatDate } from '../../utils/helpers';

// Parse summary data from notification message
// Format: "Daily Summary: ... ||{json}||"
const parseSummaryData = (message) => {
    try {
        const match = message?.match(/\|\|(.+?)\|\|/);
        if (match) {
            return JSON.parse(match[1]);
        }
    } catch (e) {
        console.warn('Failed to parse daily summary data:', e);
    }
    return null;
};

const StatCard = ({ icon: Icon, label, count, color, bgColor, borderColor }) => (
    <div className={`flex items-center gap-3 p-3 rounded-xl border ${borderColor} ${bgColor}`}>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
            <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
            <p className="text-xs text-gray-500 font-medium">{label}</p>
            <p className="text-xl font-bold text-gray-900">{count}</p>
        </div>
    </div>
);

export default function DailySummaryModal({ summaryText, date, onClose }) {
    const { t } = useLanguage();
    const summaryData = parseSummaryData(summaryText);
    
    // Use date from summaryData if available, then fallback to date prop, then current date
    const displayDate = summaryData?.date ? new Date(summaryData.date) : (date ? new Date(date) : new Date());

    // Fallback for old-style simple message
    if (!summaryData) {
        const countMatch = summaryText?.match(/\d+/);
        const count = countMatch ? countMatch[0] : '0';

        return (
            <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
            >
                <motion.div 
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    transition={{ type: "spring", stiffness: 300, damping: 25 }}
                    className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden" 
                    onClick={e => e.stopPropagation()}
                >
                    <div className="flex justify-between items-center p-4 border-b">
                        <div className="flex items-center gap-2">
                            <Calendar className="w-5 h-5 text-blue-600" />
                            <div>
                                <h2 className="text-lg font-bold text-gray-800">
                                    {t('dailySummary') || 'Daily Summary'}
                                </h2>
                                <p className="text-[10px] text-gray-500 font-normal">
                                    {formatDate(displayDate, { weekday: 'short', month: 'short', day: 'numeric' })}
                                </p>
                            </div>
                        </div>
                        <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full text-gray-500 transition-colors">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                    <div className="p-6 text-center">
                        <p className="text-gray-700">{summaryText}</p>
                    </div>
                    <div className="p-4 border-t bg-gray-50 flex justify-end">
                        <button onClick={onClose} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors">
                            {t('close') || 'Close'}
                        </button>
                    </div>
                </motion.div>
            </motion.div>
        );
    }

    const hasEmployees = summaryData.employees && summaryData.employees.length > 0;

    return (
        <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ type: "spring", stiffness: 300, damping: 25 }}
                className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex justify-between items-center p-4 border-b bg-gradient-to-r from-blue-50 to-purple-50">
                    <div className="flex items-center gap-2">
                        <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center">
                            <TrendingUp className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-900">
                                {t('dailySummary') || 'Daily Summary'}
                            </h2>
                            <p className="text-xs text-gray-500">
                                {formatDate(displayDate, { weekday: 'long', month: 'short', day: 'numeric' })}
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-white/80 rounded-full text-gray-500 transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-4 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 160px)' }}>
                    {/* Stats Grid */}
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <StatCard
                                icon={Target}
                                label={t('activeLeads') || 'Active Prequalifications'}
                                count={summaryData.activeLeads || 0}
                                color="bg-purple-100 text-purple-600"
                                bgColor="bg-purple-50/50"
                                borderColor="border-purple-100"
                            />
                            <StatCard
                                icon={FileText}
                                label={t('activeTenders') || 'Active Tenders/Proposals'}
                                count={summaryData.activeTenders || 0}
                                color="bg-indigo-100 text-indigo-600"
                                bgColor="bg-indigo-50/50"
                                borderColor="border-indigo-100"
                            />
                            <StatCard
                                icon={PhoneCall}
                                label={t('scheduledCalls') || 'Scheduled Calls'}
                                count={summaryData.scheduledCalls || 0}
                                color="bg-blue-100 text-blue-600"
                                bgColor="bg-blue-50/50"
                                borderColor="border-blue-100"
                            />
                            <StatCard
                                icon={AlertTriangle}
                                label={t('overdueCalls') || 'Overdue Calls'}
                                count={summaryData.overdueCalls || 0}
                                color="bg-red-100 text-red-600"
                                bgColor="bg-red-50/50"
                                borderColor="border-red-100"
                            />
                            <StatCard
                                icon={UserX}
                                label={t('uncontactedLeads') || 'Uncontacted Prequalifications'}
                                count={summaryData.uncontactedLeads || 0}
                                color="bg-amber-100 text-amber-600"
                                bgColor="bg-amber-50/50"
                                borderColor="border-amber-100"
                            />
                            <StatCard
                                icon={FileQuestion}
                                label={(t('uncontactedTenders') === 'uncontactedTenders' ? 'Uncontacted Tenders/Proposals' : t('uncontactedTenders')) || 'Uncontacted Tenders/Proposals'}
                                count={summaryData.uncontactedTenders || 0}
                                color="bg-orange-100 text-orange-600"
                                bgColor="bg-orange-50/50"
                                borderColor="border-orange-100"
                            />
                            <StatCard
                                icon={Clock}
                                label={(t('expiringContracts') === 'expiringContracts' ? 'Expiring Contracts (30 Days)' : t('expiringContracts')) || 'Expiring Contracts (30 Days)'}
                                count={summaryData.expiringContracts || 0}
                                color="bg-sky-100 text-sky-600"
                                bgColor="bg-sky-50/50"
                                borderColor="border-sky-100"
                            />
                            <StatCard
                                icon={AlertOctagon}
                                label={(t('expiredContracts') === 'expiredContracts' ? 'Expired Contracts' : t('expiredContracts')) || 'Expired Contracts'}
                                count={summaryData.expiredContracts || 0}
                                color="bg-rose-100 text-rose-600"
                                bgColor="bg-rose-50/50"
                                borderColor="border-rose-100"
                            />
                        </div>

                        {/* Employee Breakdown (Admin only) */}
                        {hasEmployees && (
                            <div className="mt-4">
                                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5 mb-2">
                                    <Users className="w-4 h-4 text-gray-500" />
                                    {t('employeeBreakdown') || 'Employee Breakdown'}
                                </h3>
                                <div className="bg-gray-50 rounded-xl border border-gray-100 overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="border-b border-gray-200 bg-gray-100/50">
                                                <th className="text-left py-2 px-3 font-semibold text-gray-600 text-xs">Employee</th>
                                                <th className="text-center py-2 px-2 font-semibold text-gray-600 text-xs" title="Active Prequalifications"><Target className="w-4 h-4 mx-auto" /></th>
                                                <th className="text-center py-2 px-2 font-semibold text-gray-600 text-xs" title="Active Tenders/Proposals"><FileText className="w-4 h-4 mx-auto" /></th>
                                                <th className="text-center py-2 px-2 font-semibold text-gray-600 text-xs" title="Scheduled Calls"><PhoneCall className="w-4 h-4 mx-auto" /></th>
                                                <th className="text-center py-2 px-2 font-semibold text-gray-600 text-xs" title="Overdue Calls"><AlertTriangle className="w-4 h-4 mx-auto" /></th>
                                                <th className="text-center py-2 px-2 font-semibold text-gray-600 text-xs" title="Uncontacted Prequalifications"><UserX className="w-4 h-4 mx-auto" /></th>
                                                <th className="text-center py-2 px-2 font-semibold text-gray-600 text-xs" title="Uncontacted Tenders/Proposals"><FileQuestion className="w-4 h-4 mx-auto" /></th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {summaryData.employees.map((emp, idx) => (
                                                <tr key={idx} className="hover:bg-white transition-colors">
                                                    <td className="py-2 px-3 font-medium text-gray-800 text-xs">{emp.username}</td>
                                                    <td className="text-center py-2 px-2 text-xs">
                                                        <span className={emp.activeLeads > 0 ? 'font-bold text-purple-600' : 'text-gray-400'}>{emp.activeLeads || 0}</span>
                                                    </td>
                                                    <td className="text-center py-2 px-2 text-xs">
                                                        <span className={emp.activeTenders > 0 ? 'font-bold text-indigo-600' : 'text-gray-400'}>{emp.activeTenders || 0}</span>
                                                    </td>
                                                    <td className="text-center py-2 px-2 text-xs">
                                                        <span className={emp.scheduledCalls > 0 ? 'font-bold text-blue-600' : 'text-gray-400'}>{emp.scheduledCalls}</span>
                                                    </td>
                                                    <td className="text-center py-2 px-2 text-xs">
                                                        <span className={emp.overdueCalls > 0 ? 'font-bold text-red-600' : 'text-gray-400'}>{emp.overdueCalls}</span>
                                                    </td>
                                                    <td className="text-center py-2 px-2 text-xs">
                                                        <span className={emp.uncontactedLeads > 0 ? 'font-bold text-amber-600' : 'text-gray-400'}>{emp.uncontactedLeads}</span>
                                                    </td>
                                                    <td className="text-center py-2 px-2 text-xs">
                                                        <span className={emp.uncontactedTenders > 0 ? 'font-bold text-orange-600' : 'text-gray-400'}>{emp.uncontactedTenders || 0}</span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="p-4 border-t bg-gray-50 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors"
                    >
                        {t('close') || 'Close'}
                    </button>
                </div>
            </motion.div>
        </motion.div>
    );
}
